import React from 'react';
import styles from './SalesChessGrid.module.css';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useThemeByEmpresa } from '../../../hooks/useThemeByEmpresa';

interface SalesData {
    totalSales: number;
    orderCount: number;
}

interface SalesChessGridProps {
    clients: Array<{ 
        clientId: number; 
        clientName: string; 
        city: string; 
        state: string;
        totalSales: number;
    }>;
    months: string[];
    salesData: Record<string, Record<string, SalesData>>;
    monthlyTotals: Record<string, SalesData>; // Propriedade dos totais
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSort: (column: string) => void;
    hasMore?: boolean;
    loadingMore?: boolean;
    onLoadMore?: () => void;
    startDate?: string;
    endDate?: string;
}

const SalesChessGrid: React.FC<SalesChessGridProps> = ({ 
    clients, 
    months, 
    salesData,
    monthlyTotals,
    sortBy,
    sortOrder,
    onSort,
    hasMore,
    loadingMore,
    onLoadMore,
    startDate,
    endDate
}) => {
    const [isDarkMode, setIsDarkMode] = React.useState<boolean>(() => {
        if (typeof document === 'undefined') return false;
        return document.documentElement.getAttribute('data-theme') === 'dark';
    });

    React.useEffect(() => {
        if (typeof document === 'undefined') return;

        const checkTheme = () => {
            const currentTheme = document.documentElement.getAttribute('data-theme');
            setIsDarkMode(currentTheme === 'dark');
        };

        checkTheme();

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    checkTheme();
                }
            }
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });

        return () => observer.disconnect();
    }, []);
    
    // --- Funções Helper ---
    const formatMonth = (month: string) => {
        if (!month) return '';
        if (month.startsWith('Semana')) {
            return month;
        }
        const parts = month.split('-');
        const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 
                           'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        
        if (parts.length === 3) {
            const [year, monthNum, day] = parts;
            return `${day}/${monthNames[parseInt(monthNum, 10) - 1]}`;
        }
        if (parts.length === 2) {
            const [year, monthNum] = parts;
            return `${monthNames[parseInt(monthNum, 10) - 1]}/${year.slice(2)}`;
        }
        return month;
    };

    const getWeekTooltip = (month: string) => {
        if (!month.startsWith('Semana') || !startDate) return undefined;
        const match = month.match(/Semana\s+(\d+)/);
        if (!match) return undefined;
        const w = parseInt(match[1], 10);
        
        const parts = startDate.split('-');
        if (parts.length !== 3) return undefined;
        const [year, monthNum, day] = parts.map(x => parseInt(x, 10));
        
        const startOfWeek = new Date(year, monthNum - 1, day);
        startOfWeek.setDate(startOfWeek.getDate() + (w - 1) * 7);
        
        const endOfWeek = new Date(year, monthNum - 1, day);
        endOfWeek.setDate(endOfWeek.getDate() + w * 7 - 1);
        
        if (endDate) {
            const endParts = endDate.split('-');
            if (endParts.length === 3) {
                const [eyear, emonthNum, eday] = endParts.map(x => parseInt(x, 10));
                const endLimit = new Date(eyear, emonthNum - 1, eday);
                if (endOfWeek > endLimit) {
                    endOfWeek.setTime(endLimit.getTime());
                }
            }
        }
        
        const pad = (n: number) => String(n).padStart(2, '0');
        const startStr = `${pad(startOfWeek.getDate())}/${pad(startOfWeek.getMonth() + 1)}`;
        const endStr = `${pad(endOfWeek.getDate())}/${pad(endOfWeek.getMonth() + 1)}`;
        
        return `${startStr} a ${endStr}`;
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const recentValues = Object.values(salesData ?? {}).flatMap(clientData => 
        months.map(month => clientData[month]?.totalSales || 0)
    );
    const maxValue = Math.max(...recentValues, 1);

    const getIntensity = (value: number) => {
        if (value === 0) return 0;
        return Math.min((value / maxValue) * 300, 300); 
    };

    const renderSortIcon = (column: string) => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc' ? 
            <TrendingUpIcon style={{ fontSize: 14 }} /> : 
            <TrendingDownIcon style={{ fontSize: 14 }} />;
    };

    const grandTotal = Object.values(monthlyTotals ?? {}).reduce(
        (acc, monthData) => acc + monthData.totalSales, 0
    );
    // --- Fim dos Helpers ---


    // CAMINHO 1: (Lista Vazia) Retorna um Element
    if (clients.length === 0) {
        return (
            <div className={styles.emptyState}>
                <p>Nenhum dado encontrado para os filtros aplicados</p>
            </div>
        );
    }

    // CAMINHO 2: (Lista Cheia) Retorna a Tabela (Element)
    return (
        <table className={styles.grid}>
            <thead>
                <tr>
                    <th 
                        className={styles.headerCell} // sticky: top
                        onClick={() => onSort('clientName')}
                    >
                        Cliente {renderSortIcon('clientName')}
                    </th>
                    {months.map(month => (
                        <th 
                            key={month}
                            className={styles.headerCell} // sticky: top
                            onClick={() => onSort(month)}
                            title={getWeekTooltip(month)}
                        >
                            {formatMonth(month)} {renderSortIcon(month)}
                        </th>
                    ))}
                    <th 
                        className={`${styles.headerCell} ${styles.totalHeader}`} // sticky: top
                        onClick={() => onSort('total')}
                    >
                        Total {renderSortIcon('total')}
                    </th>
                </tr>
            </thead>
            <tbody>
                {clients.map((client, clientIdx) => {
                    const isEvenRow = clientIdx % 2 === 1;
                    return (
                        <tr key={client.clientId} className={styles.row}>
                            <td className={styles.clientCell}>{client.clientName}</td>
                            {months.map(month => {
                                const cellData = salesData[client.clientId]?.[month];
                                const value = cellData?.totalSales || 0;
                                const orders = cellData?.orderCount || 0;
                                
                                let cellBg = '#ffffff';
                                let textColor = '#111827';

                                if (isDarkMode) {
                                    // Tema Escuro: Escala ajustada (exponente 0.7 e amplitude de saturação/luminosidade expandida)
                                    // para diferenciar com clareza vendas baixas, médias e altas
                                    const rawFactor = Math.min(Math.max(0, value) / maxValue, 1);
                                    const factor = Math.pow(rawFactor, 0.7);
                                    const h = 142;
                                    const s = Math.round(18 + 32 * factor);
                                    const l = Math.round(14 + 26 * factor);

                                    if (value > 0) {
                                        cellBg = `hsl(${h}, ${s}%, ${l}%)`;
                                        textColor = factor > 0.4 ? '#ffffff' : '#e5e7eb';
                                    } else if (value < 0) {
                                        cellBg = 'rgba(239, 68, 68, 0.22)';
                                        textColor = '#fca5a5';
                                    } else {
                                        cellBg = isEvenRow ? 'rgba(255, 255, 255, 0.04)' : 'var(--bg-widget)';
                                        textColor = 'var(--text-tertiary)';
                                    }
                                } else {
                                    // Tema Claro: 100% ORIGINAL para vendas > 0 ou < 0, com zebrado suave (#f8fafc) nas células sem vendas (value === 0)
                                    const intensity = getIntensity(value);
                                    const factor = intensity / 300;
                                    const h = 142; // Hue verde profissional
                                    const s = 70;  // Saturação constante de 70% para manter a cor sempre limpa e viva
                                    const l = Math.round(93 - 48 * factor); // Lightness vai de 93% (verde pastel muito claro) até 45% (verde vivo)

                                    if (value > 0) {
                                        cellBg = `hsl(${h}, ${s}%, ${l}%)`;
                                    } else if (value < 0) {
                                        cellBg = '#fef2f2'; // Vermelho pastel muito claro (red-50)
                                        textColor = '#991b1b'; // Vermelho escuro para forte contraste
                                    } else {
                                        cellBg = isEvenRow ? '#f8fafc' : '#ffffff';
                                        textColor = '#111827';
                                    }
                                }
                            
                            return (
                                <td 
                                    key={month}
                                    className={styles.dataCell}
                                    style={{ 
                                        background: cellBg,
                                        color: textColor
                                    } as React.CSSProperties}
                                    title={orders > 0 ? `${orders} pedido(s)\n${formatCurrency(value)}` : 'Sem vendas'}
                                >
                                    {formatCurrency(value)}
                                </td>
                            );
                        })}
                        <td 
                            className={styles.totalCell}
                            style={client.totalSales < 0 ? (
                                isDarkMode 
                                    ? { color: '#fca5a5', backgroundColor: '#450a0a' }
                                    : { color: '#991b1b', backgroundColor: '#fef2f2' }
                            ) : undefined}
                        >
                            {formatCurrency(client.totalSales)}
                        </td>
                    </tr>
                );
            })}
                
                {hasMore && onLoadMore && (
                    <tr className={styles.loadMoreRow}>
                        <td colSpan={months.length + 2}>
                            <div className={styles.loadMoreContainer}>
                                <button
                                    onClick={onLoadMore}
                                    disabled={loadingMore}
                                    className={styles.loadMoreButton}
                                >
                                    {loadingMore ? 'Carregando...' : '+ Carregar Mais Clientes'}
                                </button>
                            </div>
                        </td>
                    </tr>
                )}
            </tbody>
            
            <tfoot className={styles.stickyFooter}> {/* sticky: bottom */}
                <tr className={styles.totalRow}>
                    <th className={styles.totalLabelCell}>
                        TOTAL
                    </th>
                    {months.map(month => {
                        const monthVal = monthlyTotals[month]?.totalSales || 0;
                        return (
                            <td 
                                key={`total-${month}`}
                                style={monthVal < 0 ? (
                                    isDarkMode
                                        ? { color: '#fca5a5', backgroundColor: '#450a0a' }
                                        : { color: '#991b1b', backgroundColor: '#fef2f2' }
                                ) : undefined}
                            >
                                {formatCurrency(monthVal)}
                            </td>
                        );
                    })}
                    <td 
                        className={styles.grandTotalCell}
                        style={grandTotal < 0 ? (
                            isDarkMode
                                ? { color: '#fca5a5', backgroundColor: '#450a0a' }
                                : { color: '#991b1b', backgroundColor: '#fef2f2' }
                        ) : undefined}
                    >
                        {formatCurrency(grandTotal)}
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};

export default React.memo(SalesChessGrid);